import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from './api';
import { BrandHeader } from './Brand';
import { useI18n } from './i18n';
import SiteFooter from './SiteFooter';
import { CodeStep } from './TwoFactor';

function Shell({ title, children }) {
  return (
    <div className="App">
      <BrandHeader />
      <div className="auth-layout single-card">
        <div className="auth-card">
          <h2>{title}</h2>
          {children}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

export function ForgotPasswordPage() {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState('');
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      const r = await api('/auth/forgot', { method: 'POST', body: { email } });
      setDone(r.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <Shell title={t('Forgot your password?')}>
      {done ? (
        <>
          <p className="success auth-message">{done}</p>
          <p className="field-hint">{t('The link works for 1 hour. You can close this page.')}</p>
          <p className="auth-switch"><Link to="/login">{t('Back to log in')}</Link></p>
        </>
      ) : (
        <form className="auth-form" onSubmit={submit}>
          <p className="hint">{t("Enter the email you signed up with and we'll send you a link to choose a new password.")}</p>
          <label>
            {t('Email')}
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
          </label>
          {error && <p className="error">{error}</p>}
          <button type="submit" disabled={sending}>{sending ? t('Sending…') : t('Email me a reset link')}</button>
          <p className="auth-switch">{t('Remembered it?')} <Link to="/login">{t('Log in')}</Link></p>
        </form>
      )}
    </Shell>
  );
}

export function ResetPasswordPage({ onAuthenticated }) {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ticket, setTicket] = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (password !== confirm) {
      setError(t("The two passwords don't match."));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const user = await api('/auth/reset', { method: 'POST', body: { token, password } });
      // Two-step login on: the new password is saved, and the code finishes logging in.
      if (user && user.twoFactor === true && user.ticket) {
        setTicket(user.ticket);
        setSaving(false);
        return;
      }
      onAuthenticated(user);
      navigate('/app', { replace: true });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  if (!token) {
    return (
      <Shell title={t('Reset link missing')}>
        <p className="hint">{t('This page needs the link from your reset email. Request a new one below.')}</p>
        <p className="auth-switch"><Link to="/forgot-password">{t('Request a reset link')}</Link></p>
      </Shell>
    );
  }

  if (ticket) {
    return (
      <div className="App">
        <BrandHeader />
        <div className="auth-layout single-card">
          <CodeStep ticket={ticket} onAuthenticated={(user) => { onAuthenticated(user); navigate('/app', { replace: true }); }} onRestart={() => navigate('/login', { replace: true })} />
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <Shell title={t('Choose a new password')}>
      <form className="auth-form" onSubmit={submit}>
        <label>
          {t('New password')}
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" minLength={8} required />
        </label>
        <label>
          {t('Confirm new password')}
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" minLength={8} required />
        </label>
        <p className="field-hint">{t("At least 8 characters. You'll be logged in right after.")}</p>
        {error && (
          <p className="error">
            {error}{' '}
            {/expired|invalid|used|expirado|válido|usado/i.test(error) && <Link to="/forgot-password">{t('Request a new link')}</Link>}
          </p>
        )}
        <button type="submit" disabled={saving}>{saving ? t('Saving…') : t('Save new password')}</button>
      </form>
    </Shell>
  );
}
